import React, { useMemo } from "react";
import type { SectionPortfolioCarouselProps } from "../../content/section-portfolio-carousel";
import { useContent, useLocalizedUrl } from "../../hooks";
import { Button, Carousel, SectionHeader } from "../ui";

export type PortfolioCarouselSectionProps = SectionPortfolioCarouselProps & {
    /**
     * Teil eines Kapitels statt eigenstaendige Sektion? (12.8.2026)
     *
     * Im Beweis-Kapitel der Startseite (`ChapterSection`) ist «Woran wir
     * arbeiten» der vierte und breiteste Beleg — nach den Logos, dem
     * ausformulierten Fall und den vier Kacheln. Die Kapitelueberschrift steht
     * als `h2`, diese Sektion faellt deshalb auf `h3` und ihre Karten auf `h4`.
     *
     * Flaeche und Aussenabstand entfallen; beides setzt das Kapitel.
     */
    nested?: boolean;
};

/**
 * «Woran wir arbeiten» auf der Startseite (v0.3 F5).
 *
 * Die Karten kommen aus der `portfolio`-Collection, die `LanguageContext`
 * bereits nach Sprache filtert — nicht aus dem MDX. So zieht ein neuer
 * Portfolio-Eintrag automatisch auf die Startseite, ohne dass DE und EN
 * getrennt nachgefuehrt werden muessen.
 *
 * Sortiert nach `publishedAt` absteigend: das Neueste zuerst. Die Reihenfolge
 * der `Object.values()` waere sonst die Datei-Reihenfolge und damit zufaellig.
 */
export const PortfolioCarouselSection: React.FC<PortfolioCarouselSectionProps> = ({
    badge,
    title,
    description,
    limit,
    cta,
    ariaLabel,
    nested = false,
}) => {
    const { portfolio } = useContent();
    const href = useLocalizedUrl(cta?.href ?? "/referenzen");

    const items = useMemo(() => {
        const all = Object.values(portfolio ?? {}).sort((a, b) =>
            String(b.publishedAt ?? "").localeCompare(String(a.publishedAt ?? ""))
        );
        return typeof limit === "number" ? all.slice(0, limit) : all;
    }, [portfolio, limit]);

    // Ohne Eintraege bliebe eine Ueberschrift ueber einem leeren Karussell
    // stehen — dann lieber gar keine Sektion.
    if (items.length === 0) {
        return null;
    }

    const ItemHeading = nested ? "h4" : "h3";

    return (
        <section data-testid="portfolio-carousel-section" className={nested ? "" : "bg-background-light py-24"}>
            <div className="container mx-auto px-6 md:px-12 lg:px-16">
                <SectionHeader
                    badge={nested ? undefined : badge}
                    title={title}
                    subtitle={description}
                    className="mb-12"
                    headingLevel={nested ? 3 : 2}
                />

                <Carousel
                    items={items}
                    ariaLabel={ariaLabel ?? title}
                    renderItem={item => (
                        <article className="mx-3 flex h-full flex-col rounded-2xl bg-white p-6 shadow-lg">
                            <ItemHeading className="mb-3 text-xl font-black text-primary">{item.title}</ItemHeading>
                            <p className="flex-grow leading-relaxed text-black">{item.kurzbeschreibung}</p>
                            {/* Bewusst KEIN Link pro Karte: Portfolio-Eintraege
                                haben keine Detailseite, und `/referenzen` fuehrt
                                keine Anker je Eintrag (im dist nachgezaehlt —
                                0 von 6 Slugs kommen als id vor). Ein
                                «#slug»-Link landete stumm am Seitenanfang.
                                Der Ausgang der Sektion ist der CTA darunter. */}
                        </article>
                    )}
                />

                {cta?.text && (
                    <div className="mt-12 text-center">
                        <Button href={href} variant="secondary" size="md">
                            {cta.text}
                        </Button>
                    </div>
                )}
            </div>
        </section>
    );
};

export default PortfolioCarouselSection;
